import toast from "react-hot-toast";
import {
  IRepetitiveTaskManager,
  ITaskManagement,
} from "../models/taskManagement.model";
import {
  updateRepetitiveTaskAttachments,
  updateTaskAttachments,
} from "../store/slices/taskmanagementSlice";

/**
 * Normalize simple values for comparison (IDs, dates, arrays).
 */
function normalize(val: any, key?: string) {
  if (val === undefined || val === null) return null;
  if (key === "assignee") {
    return typeof val === "object"
      ? String((val as any)._id ?? val)
      : String(val);
  }
  if (key === "followers" && Array.isArray(val)) {
    return val
      .map((v) =>
        typeof v === "object" ? String((v as any)._id ?? v) : String(v)
      )
      .sort();
  }
  if (key === "attachments" && Array.isArray(val)) {
    return val
      .map((a) =>
        typeof a === "object"
          ? String(a._id ?? a.url ?? a.name ?? JSON.stringify(a))
          : String(a)
      )
      .sort();
  }
  if (key === "startDate" || key === "endDate") {
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return null;
    return d.getTime();
  }

  if (key === "typeOfRepetition") {
    return val ? JSON.stringify(val) : null;
  }

  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
}

/**
 * Build array of { field, oldValue, newValue } for top-level fields that changed.
 */
export function buildFieldChanges(
  oldTask: ITaskManagement | IRepetitiveTaskManager,
  newTask: Partial<ITaskManagement | IRepetitiveTaskManager>,
  keysToCheck: string[] = [
    "taskName",
    "assignee",
    "followers",
    "type",
    "description",
    "priority",
    "status",
    "approxTime",
    "startDate",
    "endDate",
    "attachments",
    "typeOfRepetition",
  ]
) {
  const changes: { field: string; oldValue: any; newValue: any }[] = [];

  keysToCheck.forEach((key) => {
    const oldVal = (oldTask as any)[key];
    const newVal = (newTask as any)[key];

    const nOld = normalize(oldVal, key);
    const nNew = normalize(newVal, key);

    const changed =
      Array.isArray(nOld) || Array.isArray(nNew)
        ? JSON.stringify(nOld) !== JSON.stringify(nNew)
        : nOld !== nNew;

    if (changed) {
      changes.push({
        field: key,
        oldValue: oldVal === undefined ? null : oldVal,
        newValue: newVal === undefined ? null : newVal,
      });
    }
  });

  return changes;
}

// --------------------------------------------------------------------------------------

export type FormChangeEvent = React.ChangeEvent<
  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
>;

export const handleFormChangeUtil = (
  e: FormChangeEvent,
  form: ITaskManagement,
  setForm: React.Dispatch<React.SetStateAction<ITaskManagement>>
) => {
  const { name, value } = e.target;
  setForm((prev) => ({ ...prev, [name]: value }));
};

export const removeAttachment = async (
  dispatch: any,
  taskId: string,
  attachmentId: string,
  isRepetitive: boolean = false
) => {
  if (!taskId) return;

  try {
    const payload = { removeAttachment: [attachmentId] };

    if (isRepetitive) {
      await dispatch(
        updateRepetitiveTaskAttachments({ taskId, payload })
      ).unwrap();
    } else {
      await dispatch(updateTaskAttachments({ taskId, payload })).unwrap();
    }
    return true;
  } catch (error) {
    console.error("Failed to remove attachment:", error);
    toast.error("Failed to remove attachment");
    throw error;
  }
};
